import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePeriodicLoadData } from "../hooks/usePeriodicLoadData";
import { useNavigate } from "react-router-dom";
import * as utils from "../utils/utils";
import { canActAsPatient } from "../utils/utils";
import { fetchDependents } from "../apis/api.dependents";
import { queryDependentsKey } from "../apis/queryKeys";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/common/Card";
import { Badge } from "../components/common/Badge";
import { ConsultationModeBadge } from "./ConsultationMode";
import Button from "../components/common/Button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogClose,
} from "../components/common/Dialog";
import { CalendarDays, Clock, DollarSign, User, ArrowLeft, X } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { useData } from "../lib/DataContext";
import { formatClockTimeRange, formatDateDMY, formatDateTimeDMY } from "../lib/ScheduleUtils";
import useBrandTranslation from "../hooks/useBrandTranslation";
import customToast from "./common/Toaster";
import { cancelAppointment as cancelAppointmentApi } from "../apis/api.appointments";

export default function PatientAppointments() {
  const { t } = useBrandTranslation();
  const { user } = useAuth();
  const { appointments, profiles, loadData } = useData();
  const [appointmentToCancel, setAppointmentToCancel] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);

  usePeriodicLoadData(loadData, undefined, !!user?.id && canActAsPatient(user));
  const navigation = useNavigate();

  // Familiares del titular: sus turnos también se muestran acá.
  const { data: dependents = [] } = useQuery({
    queryKey: queryDependentsKey(user?.id),
    queryFn: () => fetchDependents(user.id),
    enabled: !!user?.id && canActAsPatient(user),
  });

  const myPatientIds = useMemo(
    () => new Set([user?.id, ...dependents.map((d) => d.id)].filter(Boolean)),
    [user?.id, dependents]
  );

  const handleCancelAppointment = async () => {
    if (!appointmentToCancel || !user) return;
    setIsCancelling(true);
    try {
      await cancelAppointmentApi({
        appointmentId: appointmentToCancel.id,
        cancelledBy: user.id,
      });
      customToast(
        t("app.appointments.cancelSuccess", "Turno cancelado correctamente"),
        "success"
      );
      setAppointmentToCancel(null);
      await loadData();
    } catch (err) {
      customToast(
        err.message || t("app.appointments.cancelError", "Error al cancelar el turno"),
        "danger"
      );
    } finally {
      setIsCancelling(false);
    }
  };

  const patientAppointments = useMemo(() => {
    if (
      !user ||
      !canActAsPatient(user) ||
      !appointments ||
      appointments.length === 0
    )
      return [];

    return appointments
      .filter(
        (apt) =>
          myPatientIds.has(apt.patient_id) || apt.patient_name === user.name
      )
      .sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateB.getTime() - dateA.getTime(); // Más recientes primero
      });
  }, [appointments, user, myPatientIds]);

  if (!user || !canActAsPatient(user)) return null;

  // Próximas citas
  const upcomingAppointments = useMemo(() => {
    if (!patientAppointments || patientAppointments.length === 0) return [];

    const today = new Date();
    return patientAppointments
      .filter((apt) => {
        const aptDate = new Date(apt.date);
        return apt.status === "booked" && aptDate >= today;
      })
      .sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateA.getTime() - dateB.getTime();
      })
      .slice(0, 3); // Solo las próximas 3
  }, [patientAppointments]);

  // Historial de citas
  const pastAppointments = useMemo(() => {
    if (!patientAppointments || patientAppointments.length === 0) return [];

    const today = new Date();
    return patientAppointments
      .filter((apt) => {
        const aptDate = new Date(apt.date);
        return apt.status === "completed" || aptDate < today;
      })
      .slice(0, 5); // Solo las últimas 5
  }, [patientAppointments]);

  const getDoctorName = (doctorId) => {
    const doctor = profiles?.find((doc) => doc.id === doctorId);
    if (!doctor) return t("app.appointments.unknownDoctor", "Médico Desconocido");
    return (
      `${doctor.name || ""} ${doctor.last_name || ""}`.trim() ||
      t("app.appointments.unknownDoctor", "Médico Desconocido")
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="outline"
              onClick={() => utils.goBack()}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />{t("common.backHome", "Volver al Inicio")}</Button>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t("app.appointments.myAppointments", "Mis Turnos")}</h1>
          <p className="text-gray-600">
            {t("app.appointments.patientPageSubtitle", "Revisa tus citas médicas programadas e historial")}
          </p>
        </div>

        {/* Estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                {t("app.appointments.upcoming", "Próximos Turnos")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {upcomingAppointments.length}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                {t("app.appointments.totalAppointmentsLabel", "Total de Turnos")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {patientAppointments.length}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                {t("app.appointments.completedLabel", "Completados")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {pastAppointments.length}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" style={{ marginBottom: "100px !important" }}>
          {/* Próximos Turnos */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5" />
                {t("app.appointments.upcoming", "Próximos Turnos")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {upcomingAppointments.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">
                    {t("app.appointments.noUpcomingScheduled", "No tienes turnos programados")}
                  </p>
                  <Button onClick={() => navigation("/patient/search")}>
                    {t("app.appointments.bookAppointment", "Reservar Turno")}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {upcomingAppointments.map((appointment) => (
                    <AppointmentCard
                      key={appointment.id}
                      appointment={appointment}
                      getDoctorName={getDoctorName}
                      selfPatientId={user.id}
                      onCancel={() => setAppointmentToCancel(appointment)}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Historial */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                {t("app.appointments.recentHistory", "Historial Reciente")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pastAppointments.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  {t("app.appointments.noAppointmentHistory", "No tienes historial de turnos")}
                </p>
              ) : (
                <div className="space-y-3">
                  {pastAppointments.map((appointment) => (
                    <AppointmentCard
                      key={appointment.id}
                      appointment={appointment}
                      getDoctorName={getDoctorName}
                      selfPatientId={user.id}
                      isPast={true}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Todos los Turnos */}
        <Card className="mt-6 mb-20">
          <CardHeader>
            <CardTitle>{t("app.appointments.myAppointments", "Todos mis Turnos")}</CardTitle>
          </CardHeader>
          <CardContent>
            {patientAppointments.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-4">
                  {t("app.appointments.noRegisteredAppointments", "No tienes turnos registrados")}
                </p>
                <Button onClick={() => navigation("/patient/book")}>
                  {t("app.appointments.bookFirstAppointment", "Reservar tu Primer Turno")}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {patientAppointments.map((appointment) => (
                  <DetailedAppointmentCard
                    key={appointment.id}
                    appointment={appointment}
                    getDoctorName={getDoctorName}
                    selfPatientId={user.id}
                    onCancel={() => setAppointmentToCancel(appointment)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={!!appointmentToCancel}
        onOpenChange={(open) => !open && setAppointmentToCancel(null)}
      >
        <DialogContent className="w-[95vw] max-w-md rounded-xl p-6">
          <DialogTitle className="text-lg font-bold text-gray-900 mb-4">
            {t(
              "app.appointments.confirmCancelGeneric",
              "¿Estás seguro que deseas cancelar?"
            )}
          </DialogTitle>
          <p className="text-gray-600 text-sm mb-6">
            {t(
              "app.appointments.confirmCancelDesc",
              "Esta acción liberará el horario para otros pacientes."
            )}
          </p>
          <div className="flex gap-3 justify-end">
            <DialogClose asChild>
              <Button variant="outline" disabled={isCancelling}>
                {t("common.cancel", "Cancelar")}
              </Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={handleCancelAppointment}
              disabled={isCancelling}
            >
              {isCancelling
                ? "..."
                : t("app.appointments.confirmYes", "Sí, cancelar")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function getStatusLabel(status, t) {
  switch (status) {
    case "booked":
    case "confirmed":
      return {
        text: t("app.appointments.statusOccupied", "Ocupado"),
        variant: "default",
        className: "bg-red-100 text-red-800 border-red-200",
      };
    case "available":
      return {
        text: t("app.appointments.statusAvailable", "Libre"),
        variant: "outline",
        className: "bg-green-100 text-green-800 border-green-200",
      };
    case "completed":
      return {
        text: t("app.appointments.statusCompleted", "Completado"),
        variant: "secondary",
        className: "bg-gray-100 text-gray-800 border-gray-200",
      };
    case "cancelled":
      return {
        text: t("app.appointments.statusCancelled", "Cancelado"),
        variant: "secondary",
        className: "bg-yellow-100 text-yellow-800 border-yellow-200",
      };
    default:
      return {
        text: status || t("app.appointments.statusUnknown", "Sin estado"),
        variant: "outline",
        className: "",
      };
  }
}

function AppointmentCard({ appointment, getDoctorName, selfPatientId, isPast = false, onCancel }) {
  const { t } = useBrandTranslation();
  const statusInfo = getStatusLabel(appointment.status, t);
  const canCancel = !isPast && appointment.status === "booked";
  const forDependent =
    appointment.patient_id && appointment.patient_id !== selfPatientId;

  return (
    <div
      className={`border rounded-lg p-3 space-y-2 ${
        isPast ? "opacity-75" : ""
      }`}
    >
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-gray-500" />
          <span className="font-medium text-sm">
            {getDoctorName(appointment.doctor_id)}
          </span>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge
            variant={
              appointment.appointment_type === "private"
                ? "appointmentPrivate"
                : "appointmentInsurance"
            }
            className="text-xs"
          >
            {appointment.appointment_type === "private"
              ? t("schedule.appointmentTypes.private", "Particular")
              : t("schedule.appointmentTypes.insurance", "Obra Social")}
          </Badge>
          <ConsultationModeBadge
            mode={appointment.consultation_mode}
            className="text-xs"
          />
          <Badge variant={statusInfo.variant} className={`text-xs ${statusInfo.className}`}>
            {statusInfo.text}
          </Badge>
        </div>
      </div>

      {forDependent && (
        <div className="text-xs text-slate-500">
          {t("app.appointments.forPatient", "Para: {{name}}", {
            name: appointment.patient_name,
          })}
        </div>
      )}

      <div className="space-y-1 text-sm">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-3 h-3 text-gray-500" />
          <span>
            {formatDateDMY(appointment.date)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-3 h-3 text-gray-500" />
          <span>
            {formatClockTimeRange(appointment.start_time, appointment.end_time)}
          </span>
        </div>
      </div>

      {appointment.price && (
        <div className="flex items-center gap-1 text-xs text-green-600 font-medium">
          <DollarSign className="w-3 h-3" />
          {appointment.price}
        </div>
      )}

      {canCancel && onCancel && (
        <div className="flex justify-end pt-1">
          <Button variant="outline" size="sm" onClick={onCancel}>
            <X className="w-3 h-3 mr-1" />
            {t("app.appointments.cancelAppointment", "Cancelar turno")}
          </Button>
        </div>
      )}
    </div>
  );
}

function DetailedAppointmentCard({ appointment, getDoctorName, selfPatientId, onCancel }) {
  const { t } = useBrandTranslation();
  const isPast =
    new Date(appointment.date) < new Date() ||
    appointment.status === "completed";
  const statusInfo = getStatusLabel(appointment.status, t);
  const canCancel = !isPast && appointment.status === "booked";
  const forDependent =
    appointment.patient_id && appointment.patient_id !== selfPatientId;

  return (
    <div
      className={`border rounded-lg p-4 space-y-3 ${
        isPast ? "opacity-75" : ""
      }`}
    >
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <User className="w-4 h-4 text-gray-500" />
            <span className="font-medium">
              {getDoctorName(appointment.doctor_id)}
            </span>
          </div>
          {forDependent && (
            <div className="text-xs text-slate-500 mb-1">
              {t("app.appointments.forPatient", "Para: {{name}}", {
                name: appointment.patient_name,
              })}
            </div>
          )}
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-1">
              <CalendarDays className="w-3 h-3" />
              <span>
                {formatDateDMY(appointment.date)}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>
                {formatClockTimeRange(appointment.start_time, appointment.end_time)}
              </span>
            </div>
          </div>
        </div>
        <div className="text-right space-y-1">
          <Badge
            variant={
              appointment.appointment_type === "private"
                ? "appointmentPrivate"
                : "appointmentInsurance"
            }
          >
            {appointment.appointment_type === "private"
              ? t("schedule.appointmentTypes.private", "Particular")
              : t("schedule.appointmentTypes.insurance", "Obra Social")}
          </Badge>
          <ConsultationModeBadge
            mode={appointment.consultation_mode}
            className="text-xs"
          />
          <div>
            <Badge variant={statusInfo.variant} className={`text-xs ${statusInfo.className}`}>
              {statusInfo.text}
            </Badge>
          </div>
          {appointment.price && (
            <div className="flex items-center gap-1 text-sm text-green-600 font-medium justify-end">
              <DollarSign className="w-3 h-3" />
              {appointment.price}
            </div>
          )}
        </div>
      </div>

      {appointment.notes && (
        <div className="border-t pt-3">
          <div className="text-sm text-gray-600">
            <span className="font-medium">{t("app.appointments.reason", "Motivo:")}</span> {appointment.notes}
          </div>
        </div>
      )}

      <div className="text-xs text-gray-500">
        {t("app.appointments.createdOn", "Creado el")}{" "}
        {formatDateTimeDMY(appointment.created_at)}
      </div>

      {canCancel && onCancel && (
        <div className="flex justify-end pt-2 border-t">
          <Button variant="outline" size="sm" onClick={onCancel}>
            <X className="w-3 h-3 mr-1" />
            {t("app.appointments.cancelAppointment", "Cancelar turno")}
          </Button>
        </div>
      )}
    </div>
  );
}
